'use client';

import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ConfigOption {
  id: string;
  option_key: string;
  label: string;
  price: number;
  description?: string | null;
  color?: string | null;
  sort_order: number;
}

interface ConfigSection {
  id: string;
  section_key: string;
  title: string;
  icon: string;
  sort_order: number;
  options: ConfigOption[];
}

interface ContactInfo {
  name: string;
  company: string;
  email: string;
  phone: string;
  notes: string;
}

// ─── Cab Preview SVG ──────────────────────────────────────────────────────────

function CabPreview({ selections, sections }: { selections: Record<string, string>; sections: ConfigSection[] }) {
  const getOptionByKey = (sectionKey: string, optionKey: string) =>
    sections.find(s => s.section_key === sectionKey)?.options.find(o => o.option_key === optionKey);

  const wallColor = getOptionByKey('wallFinish', selections.wallFinish)?.color ?? '#F5F5F5';
  const floorColor = getOptionByKey('floorFinish', selections.floorFinish)?.color ?? '#9E9E9E';
  const hasHandrail = selections.handrail && selections.handrail !== 'none';
  const hasGlass = selections.gate === 'glass_swing';
  const hasMirror = selections.wallFinish === 'mirror';
  const hasLED = selections.ceiling === 'led_panel' || selections.lighting === 'cove_lighting';

  return (
    <div className="relative w-full aspect-[3/4] max-w-xs mx-auto">
      <svg viewBox="0 0 240 320" className="w-full h-full drop-shadow-2xl" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="wallGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={wallColor} stopOpacity="0.7" />
            <stop offset="100%" stopColor={wallColor} />
          </linearGradient>
          <linearGradient id="floorGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={floorColor} stopOpacity="0.9" />
            <stop offset="100%" stopColor={floorColor} stopOpacity="0.6" />
          </linearGradient>
          {hasLED && (
            <radialGradient id="ledGlow" cx="50%" cy="0%" r="60%">
              <stop offset="0%" stopColor="#FFF9C4" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#FFF9C4" stopOpacity="0" />
            </radialGradient>
          )}
          {hasMirror && (
            <linearGradient id="mirrorSheen" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.4" />
              <stop offset="50%" stopColor={wallColor} stopOpacity="0.8" />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.2" />
            </linearGradient>
          )}
        </defs>

        <rect x="20" y="20" width="200" height="240" fill={hasMirror ? 'url(#mirrorSheen)' : 'url(#wallGrad)'} rx="2" />
        <polygon points="20,20 0,40 0,280 20,260" fill={wallColor} opacity="0.6" />
        <polygon points="220,20 240,40 240,280 220,260" fill={wallColor} opacity="0.4" />
        <polygon points="20,20 220,20 240,40 0,40" fill={
          selections.ceiling === 'brushed_steel_ceiling' ? '#B0BEC5' :
          selections.ceiling === 'wood_panel_ceiling' ? '#C8A96E' : '#EEEEEE'
        } />
        {hasLED && <rect x="20" y="20" width="200" height="240" fill="url(#ledGlow)" />}
        <polygon points="20,260 220,260 240,280 0,280" fill="url(#floorGrad)" />
        {(selections.floorFinish === 'hardwood_oak' || selections.floorFinish === 'tile_slate') && (
          <>
            <line x1="20" y1="265" x2="220" y2="265" stroke="rgba(0,0,0,0.15)" strokeWidth="1" />
            <line x1="20" y1="272" x2="220" y2="272" stroke="rgba(0,0,0,0.1)" strokeWidth="0.5" />
          </>
        )}
        {hasHandrail && (
          <rect x="25" y="160" width="190" height="8" rx="4"
            fill={
              selections.handrail === 'chrome_round' ? '#E0E0E0' :
              selections.handrail === 'brushed_nickel' ? '#C8B89A' :
              selections.handrail === 'wood_rail' ? '#A0724A' : '#333333'
            }
            opacity="0.9"
          />
        )}
        <rect x="170" y="100" width="30" height="60" rx="3" fill="#1A1A1A" opacity="0.85" />
        {selections.operatingPanel === 'touchscreen' ? (
          <rect x="173" y="103" width="24" height="40" rx="2" fill="#1565C0" opacity="0.7" />
        ) : (
          <>
            <circle cx="185" cy="115" r="4" fill="#4CAF50" opacity="0.9" />
            <circle cx="185" cy="130" r="4" fill="#FFC107" opacity="0.9" />
            <circle cx="185" cy="145" r="4" fill="#F44336" opacity="0.9" />
          </>
        )}
        {selections.gate === 'scissor_gate' && (
          <>
            <line x1="20" y1="20" x2="120" y2="260" stroke="#757575" strokeWidth="2" opacity="0.5" />
            <line x1="120" y1="20" x2="20" y2="260" stroke="#757575" strokeWidth="2" opacity="0.5" />
            <line x1="120" y1="20" x2="220" y2="260" stroke="#757575" strokeWidth="2" opacity="0.5" />
            <line x1="220" y1="20" x2="120" y2="260" stroke="#757575" strokeWidth="2" opacity="0.5" />
          </>
        )}
        {(selections.gate === 'solid_swing' || selections.gate === 'bi_fold') && (
          <rect x="20" y="20" width="200" height="240" fill="#37474F" opacity="0.15" rx="1" />
        )}
        {hasGlass && (
          <rect x="20" y="20" width="200" height="240" fill="#B3E5FC" opacity="0.2" rx="1" />
        )}
        {selections.lighting === 'pendant' ? (
          <>
            <line x1="120" y1="30" x2="120" y2="60" stroke="#9E9E9E" strokeWidth="2" />
            <ellipse cx="120" cy="65" rx="18" ry="8" fill="#FFF9C4" opacity="0.9" />
          </>
        ) : (
          <rect x="80" y="28" width="80" height="10" rx="5" fill={hasLED ? '#FFF9C4' : '#EEEEEE'} opacity="0.9" />
        )}
        {hasMirror && (
          <rect x="25" y="25" width="190" height="230" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1" rx="1" />
        )}
        <rect x="20" y="20" width="200" height="240" fill="none" stroke="#333" strokeWidth="2" rx="2" />
        <polygon points="20,20 220,20 240,40 0,40" fill="none" stroke="#333" strokeWidth="1.5" />
        <polygon points="20,260 220,260 240,280 0,280" fill="none" stroke="#333" strokeWidth="1.5" />
        <polygon points="20,20 0,40 0,280 20,260" fill="none" stroke="#333" strokeWidth="1.5" />
        <polygon points="220,20 240,40 240,280 220,260" fill="none" stroke="#333" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

// ─── BOM Row ──────────────────────────────────────────────────────────────────

function BOMRow({ label, option, price }: { label: string; option: string; price: number }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
        <p className="text-sm font-medium text-gray-900 mt-0.5">{option}</p>
      </div>
      <span className="text-sm font-semibold text-gray-800 ml-4 whitespace-nowrap">
        {price === 0 ? <span className="text-gray-400 font-normal">Included</span> : `+$${price.toLocaleString()}`}
      </span>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CabConfiguratorPage() {
  const { user } = useAuth();
  const router = useRouter();

  const [sections, setSections] = useState<ConfigSection[]>([]);
  const [basePrice, setBasePrice] = useState(8500);
  const [configLoading, setConfigLoading] = useState(true);
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState('');
  const [step, setStep] = useState<'configure' | 'review' | 'contact' | 'success'>('configure');
  const [contact, setContact] = useState<ContactInfo>({ name: '', company: '', email: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Load configuration from Supabase
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setConfigLoading(true);

        const [sectionsRes, optionsRes, settingsRes] = await Promise.all([
          supabase
            .from('cab_configurator_sections')
            .select('*')
            .eq('is_active', true)
            .order('sort_order'),
          supabase
            .from('cab_configurator_options')
            .select('*')
            .eq('is_active', true)
            .order('sort_order'),
          supabase
            .from('cab_configurator_settings')
            .select('*')
            .eq('setting_key', 'base_price')
            .single(),
        ]);

        const rawSections = sectionsRes.data || [];
        const rawOptions = optionsRes.data || [];

        // Attach options to sections
        const builtSections: ConfigSection[] = rawSections.map(sec => ({
          ...sec,
          options: rawOptions.filter(o => o.section_id === sec.id),
        }));

        setSections(builtSections);

        if (settingsRes.data) {
          setBasePrice(parseFloat(settingsRes.data.setting_value) || 8500);
        }

        // Set default selections (first option of each section)
        const defaults: Record<string, string> = {};
        builtSections.forEach(sec => {
          if (sec.options.length > 0) {
            defaults[sec.section_key] = sec.options[0].option_key;
          }
        });
        setSelections(defaults);

        if (builtSections.length > 0) {
          setActiveSection(builtSections[0].section_key);
        }
      } catch (err) {
        console.error('Failed to load cab configurator config:', err);
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, []);

  const selectedOptions = useMemo(() => {
    return sections.map(section => {
      const option = section.options.find(o => o.option_key === selections[section.section_key]);
      return { section, option: option || section.options[0] };
    }).filter(({ option }) => option != null);
  }, [selections, sections]);

  const totalPrice = useMemo(() => {
    return basePrice + selectedOptions.reduce((sum, { option }) => sum + (option?.price ?? 0), 0);
  }, [selectedOptions, basePrice]);

  const handleSelect = (sectionKey: string, optionKey: string) => {
    setSelections(prev => ({ ...prev, [sectionKey]: optionKey }));
  };

  const handleSubmit = async () => {
    if (!contact.name || !contact.email) {
      setSubmitError('Name and email are required.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');

    try {
      const configSummary = selectedOptions.map(({ section, option }) =>
        `${section.title}: ${option?.label} (+$${option?.price ?? 0})`
      ).join('\n');

      const orderData = {
        company_name: contact.company || contact.name,
        contact_name: contact.name,
        email: contact.email,
        phone: contact.phone || null,
        notes: `CAB CONFIGURATOR ORDER\n\nConfiguration:\n${configSummary}\n\nBase Price: $${basePrice.toLocaleString()}\nTotal: $${totalPrice.toLocaleString()}\n\nCustomer Notes:\n${contact.notes}`,
        order_total: totalPrice,
        status: 'pending',
        user_id: user?.id || null,
      };

      const { error } = await supabase.from('catalog_orders').insert([orderData]);
      if (error) throw error;

      setStep('success');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetConfig = () => {
    const defaults: Record<string, string> = {};
    sections.forEach(sec => {
      if (sec.options.length > 0) defaults[sec.section_key] = sec.options[0].option_key;
    });
    setSelections(defaults);
    setContact({ name: '', company: '', email: '', phone: '', notes: '' });
    setStep('configure');
  };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (configLoading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mb-4"></div>
            <p className="text-gray-600">Loading configurator...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ── Success Screen ──────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-lg w-full text-center py-20">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={40} className="text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Configuration Submitted!</h1>
            <p className="text-gray-600 mb-2">
              Thank you, <strong>{contact.name}</strong>. Your custom cab configuration has been received.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              Our team will review your selections and reach out to <strong>{contact.email}</strong> within 1–2 business days with a formal quote.
            </p>
            <div className="bg-white border border-gray-200 rounded-lg p-5 text-left mb-8">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Estimated Total</p>
              <p className="text-3xl font-bold text-gray-900">${totalPrice.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1">Final pricing subject to site survey and installation requirements</p>
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={resetConfig}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                New Configuration
              </button>
              <Link href="/homepage" className="px-6 py-3 bg-black text-white font-medium hover:bg-gray-900 transition-colors">
                Return Home
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ── Contact Step ────────────────────────────────────────────────────────────
  if (step === 'contact') {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <button onClick={() => setStep('review')} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-8 transition-colors text-sm">
              <Icon name="ArrowLeftIcon" size={16} />
              Back to Review
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">Your Contact Details</h1>
                <p className="text-gray-500 text-sm mb-6">We'll use this to prepare your formal quote.</p>

                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1.5">Full Name *</label>
                      <input type="text" value={contact.name} onChange={e => setContact(p => ({ ...p, name: e.target.value }))}
                        className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors" placeholder="John Smith" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1.5">Company</label>
                      <input type="text" value={contact.company} onChange={e => setContact(p => ({ ...p, company: e.target.value }))}
                        className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors" placeholder="Acme Corp" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1.5">Email *</label>
                      <input type="email" value={contact.email} onChange={e => setContact(p => ({ ...p, email: e.target.value }))}
                        className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors" placeholder="john@example.com" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1.5">Phone</label>
                      <input type="tel" value={contact.phone} onChange={e => setContact(p => ({ ...p, phone: e.target.value }))}
                        className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors" placeholder="(555) 000-0000" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 uppercase tracking-wider mb-1.5">Additional Notes</label>
                    <textarea value={contact.notes} onChange={e => setContact(p => ({ ...p, notes: e.target.value }))}
                      rows={4} className="w-full border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black transition-colors resize-none"
                      placeholder="Any special requirements, site conditions, or questions..." />
                  </div>

                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded">{submitError}</div>
                  )}

                  <button onClick={handleSubmit} disabled={submitting}
                    className="w-full bg-red-600 text-white py-3.5 font-semibold text-sm tracking-wide hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {submitting ? (
                      <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>Submitting...</>
                    ) : (
                      <><Icon name="PaperAirplaneIcon" size={16} />Submit Configuration for Quote</>
                    )}
                  </button>
                  <p className="text-xs text-gray-400 text-center">No payment required. We'll contact you with a formal quote.</p>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-28">
                  <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Order Summary</h2>
                  <CabPreview selections={selections} sections={sections} />
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Estimated Total</span>
                      <span className="text-xl font-bold text-gray-900">${totalPrice.toLocaleString()}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Subject to final site survey</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ── Review Step ─────────────────────────────────────────────────────────────
  if (step === 'review') {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <button onClick={() => setStep('configure')} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-8 transition-colors text-sm">
              <Icon name="ArrowLeftIcon" size={16} />
              Back to Configurator
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">Review Your Configuration</h1>
                <p className="text-gray-500 text-sm mb-6">Confirm your selections before submitting for a quote.</p>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Base Cab Price</span>
                    <span className="text-sm font-semibold text-gray-800">${basePrice.toLocaleString()}</span>
                  </div>
                  {selectedOptions.map(({ section, option }) => option && (
                    <BOMRow key={section.id} label={section.title} option={option.label} price={option.price} />
                  ))}
                  <div className="mt-4 pt-4 border-t-2 border-gray-900 flex items-center justify-between">
                    <span className="font-bold text-gray-900">Estimated Total</span>
                    <span className="text-2xl font-bold text-gray-900">${totalPrice.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Final pricing subject to site survey and installation requirements.</p>
                </div>

                <button onClick={() => setStep('contact')}
                  className="mt-6 w-full bg-black text-white py-4 font-semibold text-sm tracking-wide hover:bg-gray-900 transition-colors flex items-center justify-center gap-2">
                  Continue to Submit
                  <Icon name="ArrowRightIcon" size={16} />
                </button>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-28">
                  <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Cab Preview</h2>
                  <CabPreview selections={selections} sections={sections} />
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ── Configure Step (main) ───────────────────────────────────────────────────
  const activeSectionData = sections.find(s => s.section_key === activeSection);

  if (!activeSectionData) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 flex items-center justify-center">
          <p className="text-gray-500">No configuration options available. Please check back later.</p>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50">
        <div className="bg-black text-white py-10 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
              <Link href="/homepage" className="hover:text-white transition-colors">Home</Link>
              <span>/</span>
              <span className="text-white">Cab Configurator</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">Cab Configurator</h1>
            <p className="text-gray-400 max-w-xl">
              Design your custom elevator cab. Select materials, finishes, and components — see a live preview and get an instant price estimate.
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

            <div className="lg:col-span-3 space-y-6">
              {/* Section tabs */}
              <div className="flex flex-wrap gap-2">
                {sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.section_key)}
                    className={`px-3 py-1.5 text-xs font-medium tracking-wide border transition-all ${
                      activeSection === section.section_key
                        ? 'bg-black text-white border-black' :'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>

              {/* Active section options */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">{activeSectionData.title}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activeSectionData.options.map(option => {
                    const isSelected = selections[activeSection] === option.option_key;
                    return (
                      <button
                        key={option.id}
                        onClick={() => handleSelect(activeSection, option.option_key)}
                        className={`relative text-left p-4 border-2 transition-all rounded-sm ${
                          isSelected
                            ? 'border-black bg-gray-50' :'border-gray-200 hover:border-gray-400 bg-white'
                        }`}
                      >
                        {option.color && (
                          <div className="w-6 h-6 rounded-full border border-gray-200 mb-2" style={{ backgroundColor: option.color }} />
                        )}
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{option.label}</p>
                            {option.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                            )}
                          </div>
                          <span className={`text-xs font-medium whitespace-nowrap mt-0.5 ${option.price === 0 ? 'text-gray-400' : 'text-gray-700'}`}>
                            {option.price === 0 ? 'Included' : `+$${option.price.toLocaleString()}`}
                          </span>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2">
                            <Icon name="CheckCircleIcon" size={16} className="text-black" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Section navigation */}
              <div className="flex justify-between items-center">
                <button
                  onClick={() => {
                    const idx = sections.findIndex(s => s.section_key === activeSection);
                    if (idx > 0) setActiveSection(sections[idx - 1].section_key);
                  }}
                  disabled={sections[0]?.section_key === activeSection}
                  className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <Icon name="ArrowLeftIcon" size={14} />
                  Previous
                </button>
                {sections[sections.length - 1]?.section_key === activeSection ? (
                  <button onClick={() => setStep('review')}
                    className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors">
                    Review & Quote
                    <Icon name="ArrowRightIcon" size={14} />
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      const idx = sections.findIndex(s => s.section_key === activeSection);
                      setActiveSection(sections[idx + 1].section_key);
                    }}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    Next
                    <Icon name="ArrowRightIcon" size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Right: Preview + live BOM */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-28">
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Live Preview</h2>
                <CabPreview selections={selections} sections={sections} />

                <div className="mt-5 pt-4 border-t border-gray-100 space-y-0">
                  {selectedOptions.map(({ section, option }) => option && (
                    <div key={section.id} className="flex justify-between items-center py-1.5 border-b border-gray-50 last:border-0">
                      <span className="text-xs text-gray-500">{section.title}</span>
                      <span className="text-xs font-medium text-gray-800 text-right max-w-[55%] truncate">{option.label}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t-2 border-gray-900">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Estimated Total</p>
                      <p className="text-2xl font-bold text-gray-900 mt-0.5">${totalPrice.toLocaleString()}</p>
                    </div>
                    <button onClick={() => setStep('review')}
                      className="px-4 py-2 bg-black text-white text-xs font-semibold hover:bg-gray-900 transition-colors">
                      Get Quote
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Base price: ${basePrice.toLocaleString()} + selected options</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
